import React from 'react';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { TaskItem } from '../types/dashboardTypes';
import { X } from 'lucide-react';
import { useCalendarPreferences } from '../../../hooks/useCalendarPreferences';

interface DatePickerModalProps {
  isOpen: boolean;
  task: TaskItem | null;
  onClose: () => void;
  onDateSelect: (date: Date | null) => void;
}

/**
 * Modal component that displays a date picker
 * Used when dragging a task to the "This Week" column
 */
const DatePickerModal: React.FC<DatePickerModalProps> = ({
  isOpen,
  task,
  onClose,
  onDateSelect,
}) => {
  // Use the user's calendar preferences
  const { datePickerLocale } = useCalendarPreferences();

  // Don't render anything if the modal is not open or no task is provided
  if (!isOpen || !task) {
    return null;
  }

  // Calculate the min and max dates for "This Week"
  const today = new Date();
  const startOfDay = new Date(today);
  startOfDay.setHours(0, 0, 0, 0);

  const endOfWeek = new Date(today);
  endOfWeek.setDate(today.getDate() + 7);
  endOfWeek.setHours(23, 59, 59, 999);

  // Render standard modal
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-6 w-96 max-w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">Select a deadline</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={20} />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Select a date for: <span className="font-medium">{task.title}</span>
          </p>
          <ReactDatePicker
            selected={today}
            onChange={onDateSelect}
            inline
            minDate={startOfDay}
            maxDate={endOfWeek}
            dateFormat="MMMM d, yyyy"
            calendarClassName="w-full"
            highlightDates={[today]}
            locale={datePickerLocale}
          />
        </div>

        <div className="flex justify-between">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 bg-gray-200 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={() => onDateSelect(today)}
            className="px-4 py-2 text-white bg-blue-500 rounded-md hover:bg-blue-600"
          >
            Today
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatePickerModal;
